import React, { useState, useEffect, useRef } from 'react'
import Checkbox from '@mui/material/Checkbox'
import './SelectComponent.scss'

interface Props {
  selected?: number
  options: string[]
  defaultValue: string
  multiSelect?: boolean
  reset?: boolean
  className?: string
  value?: string | number
  onChange?: (value: string | string[], index?: number) => void
  onSelect: (selected: string | string[], index?: number | boolean) => void
}

const Select: React.FC<Props> = (props) => {
  const [buttonText, setButtonText] = useState(
    props.value !== undefined
      ? String(props.value)
      : typeof props.selected === 'number' && props.options[props.selected]
        ? props.options[props.selected]
        : props.defaultValue
  )
  const [toggleValue, setToggle] = useState(false)
  const [options, setOptions] = useState<Array<Record<string, boolean>>>([])
  const [active, setactive] = useState(typeof props.selected === 'number' ? props.selected : 0)
  const [error, setError] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(typeof props.selected === 'number' ? props.selected : null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const optionsRef = useRef(options)
  optionsRef.current = options

  const handlebuttonClick = (event: React.MouseEvent) => {
    console.log('evnt triggered', event)
    setToggle(!toggleValue)
    event.preventDefault()
  }

  const handleOption = (optionIndex: number, optionText: string) => {
    console.log('menu item click', optionIndex, optionText)
    const { multiSelect } = props
    if (multiSelect) {
      const updatedOptions = [...options]
      updatedOptions[optionIndex] = {
        ...updatedOptions[optionIndex],
        [optionText]: !updatedOptions[optionIndex]?.[optionText]
      }
      const selOptions: string[] = []
      updatedOptions.forEach((option) => {
        if (Object.values(option)[0]) {
          selOptions.push(Object.keys(option)[0])
        }
      })
      setToggle(!toggleValue)
      setOptions(updatedOptions)
      props.onSelect(selOptions)
      props.onChange?.(selOptions)
    } else {
      setButtonText(optionText)
      setactive(optionIndex)
      setSelectedIndex(optionIndex)
      setToggle(!toggleValue)
      if (optionIndex === 0) {
        setError(true)
      } else {
        setError(false)
      }
      props.onSelect(optionText, optionIndex)
      props.onChange?.(optionText, optionIndex)
    }
  }

  const handleCheckChange = (optionIndex: number, optionText: string, isChecked: boolean) => {
    console.log(optionIndex, optionText, isChecked)
    const updatedOptions = [...options]
    updatedOptions[optionIndex] = {
      ...updatedOptions[optionIndex],
      [optionText]: isChecked
    }
    const selOptions: string[] = []
    updatedOptions.forEach((option) => {
      if (Object.values(option)[0]) {
        selOptions.push(Object.keys(option)[0])
      }
    })
    setOptions(updatedOptions)
    props.onSelect(selOptions)
  }

  useEffect(() => {
    if (props.options && props.options.length > 0) {
      const currentKeys = optionsRef.current.map((opt) => Object.keys(opt)[0])
      const sameOptions = currentKeys.length === props.options.length && props.options.every((opt, index) => opt === currentKeys[index])
      if (!sameOptions) {
        const opts = props.options.map((option) => ({ [option]: false }))
        setOptions(opts)
      }

      if (!hasInitialized && typeof props.selected === 'number' && props.options[props.selected]) {
        setButtonText(props.options[props.selected])
        setactive(props.selected)
        setSelectedIndex(props.selected)
        props.onSelect(props.options[props.selected], props.selected)
        setHasInitialized(true)
      }
    }
  }, [props.options, props.selected, hasInitialized])

  useEffect(() => {
    if (props.value !== undefined) {
      const valueString = String(props.value)
      setButtonText(valueString)
      const index = props.options.findIndex((option) => String(option) === valueString)
      if (index >= 0) {
        setactive(index)
        setSelectedIndex(index)
      }
    }
  }, [props.value, props.options])

  useEffect(() => {
    if (props.reset) {
      const opts = props.options.map((option) => ({ [option]: false }))
      setOptions(opts)
      setButtonText(props.defaultValue)
      setactive(0)
      setSelectedIndex(null)
      setToggle(false)
      setHasInitialized(false)
      props.onSelect([], true)
    }
  }, [props.reset, props.options, props.defaultValue])

  return (
    <div className={props.className}>
      <button
        onClick={handlebuttonClick}
        className={`metlife-button${error ? ' error-btn' : ''}`}
      >
        <span className='inner-button'>{buttonText}</span>
      </button>
      {toggleValue && (
        <div className='dropdown-menu'>
          {props.options.map((option, index) => (
            <div
              key={index}
              className={`menu-item-list${active === index ? ' active' : ''}`}
              onClick={() => handleOption(index, option)}
            >
              {props.multiSelect && (
                <Checkbox
                  size='small'
                  checked={options[index] && options[index][option]}
                  onChange={(e) => handleCheckChange(index, option, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Select
